import { useEffect, useMemo, useState } from "react";
import PageLayout from "../components/PageLayout";
import {
  createIdentityBackSvg,
  createIdentityFrontSvg,
  identityArtworkMetadata
} from "../merch/artwork/identityArtwork";
import { downloadSvg, exportSvgToPng } from "../utils/merchArtworkExport";
import MerchMockupStudio from "../components/merch/MerchMockupStudio";
import { merchProducts } from "../data/merchProducts";
import { publishMerchMockup, publishMerchMockupSet } from "../services/merchProducts";
import { renderMerchMockupPng } from "../utils/merchMockupExport";
import {
  cardStyle,
  disabledButtonStyle,
  optionButtonStyle,
  primaryButtonStyle,
  sectionLabelStyle,
  selectedOptionButtonStyle
} from "../components/merch/MerchStyles";

const variantDefinitions = [
  { key: "front-dark", placement: "front", tone: "dark", title: "Front — Dark Garment", recommendedUse: "Faded Black and Faded Coal", createSvg: createIdentityFrontSvg },
  { key: "front-light", placement: "front", tone: "light", title: "Front — Light Garment", recommendedUse: "Faded Bone", createSvg: createIdentityFrontSvg },
  { key: "back-dark", placement: "back", tone: "dark", title: "Back — Dark Garment", recommendedUse: "Faded Black and Faded Coal", createSvg: createIdentityBackSvg },
  { key: "back-light", placement: "back", tone: "light", title: "Back — Light Garment", recommendedUse: "Faded Bone", createSvg: createIdentityBackSvg }
];

function MerchArtworkPreview() {
  const [studioMode, setStudioMode] = useState("production");
  const [currentMockups, setCurrentMockups] = useState([]);
  const variants = useMemo(() => variantDefinitions.map((definition) => {
    try {
      return { ...definition, svgString: definition.createSvg({ garmentTone: definition.tone }), generationError: "" };
    } catch (error) {
      console.error(`[Merch Engine] ${definition.title} generation failed:`, error);
      return { ...definition, svgString: "", generationError: error.message || "Artwork generation failed." };
    }
  }), []);

  return (
    <PageLayout maxWidth="1200px" showBack eyebrow="Merch Engine" title="Identity Artwork Preview" subtitle="Internal production-artwork review tool.">
      <PreviewResponsiveStyles />
      <div role="group" aria-label="Artwork review mode" style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "38px" }}>
        <button className="merch-option" type="button" aria-pressed={studioMode === "production"} onClick={() => setStudioMode("production")} style={studioMode === "production" ? selectedOptionButtonStyle : optionButtonStyle}>Production Artwork</button>
        <button className="merch-option" type="button" aria-pressed={studioMode === "mockup"} onClick={() => setStudioMode("mockup")} style={studioMode === "mockup" ? selectedOptionButtonStyle : optionButtonStyle}>Mockup Studio</button>
      </div>

      {studioMode === "production" ? (
        <>
          <p style={{ maxWidth: "720px", margin: "0 0 34px", color: "#999", lineHeight: 1.65 }}>
            Review the exact transparent production masters generated for the Identity Tee. Preview surfaces are for inspection only and are never included in downloads.
          </p>
          <section className="merch-artwork-grid" aria-label="Identity Tee artwork variants">
            {variants.map((variant) => <MerchArtworkPreviewCard key={variant.key} variant={variant} />)}
          </section>
        </>
      ) : <MerchMockupStudio onCurrentMockupChange={setCurrentMockups} />}

      <MockupPublishingPanel currentMockups={currentMockups} />
    </PageLayout>
  );
}

function MockupPublishingPanel({ currentMockups }) {
  const [productId, setProductId] = useState(merchProducts[0]?.id || "");
  const [mockupFile, setMockupFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!mockupFile) { setPreviewUrl(""); return undefined; }
    const nextUrl = URL.createObjectURL(mockupFile);
    setPreviewUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [mockupFile]);

  const publish = async () => {
    if (publishing) return;
    setPublishing(true);
    setMessage("");
    try {
      await publishMerchMockup({ productId, file: mockupFile });
      setMessage("Mockup published to Merch Engine.");
    } catch (error) {
      setMessage(error.message || "Mockup could not be published.");
    } finally {
      setPublishing(false);
    }
  };

  const publishCurrentMockups = async () => {
    if (publishing || currentMockups.length === 0) return;
    setPublishing(true);
    setMessage("");
    try {
      const rendered = [];
      for (const [index, mockup] of currentMockups.entries()) {
        setMessage(`Preparing mockup ${index + 1} of ${currentMockups.length}…`);
        const blob = await renderMerchMockupPng(mockup);
        const filename = `${productId}-${mockup.garmentColor}-${mockup.view}-mockup.png`
          .toLowerCase()
          .replace(/[^a-z0-9.-]/g, "-");
        rendered.push({
          file: new File([blob], filename, { type: "image/png" }),
          color: mockup.garmentColor,
          view: mockup.view
        });
      }
      setMessage("Uploading complete mockup set…");
      await publishMerchMockupSet({ productId, mockups: rendered });
      setMessage("All three colors and front/back views published to Merch Engine.");
    } catch (error) {
      setMessage(error.message || "Current mockup could not be published.");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <section style={{ ...cardStyle, marginTop: "48px", padding: "24px" }} aria-labelledby="publish-mockup-title">
      <p style={sectionLabelStyle}>Customer-Facing Approval</p>
      <h2 id="publish-mockup-title" style={{ margin: "0 0 10px" }}>Publish approved garment mockup</h2>
      <p style={{ margin: "0 0 22px", color: "#999", lineHeight: 1.6 }}>Select a product and the approved real-garment image. Production artwork remains separate and is never published by this action.</p>
      <label style={publishLabelStyle}>Product<select value={productId} onChange={(event) => setProductId(event.target.value)} style={publishInputStyle}>{merchProducts.map((product) => <option key={product.id} value={product.id}>{product.name} — {product.status || "published"}</option>)}</select></label>
      <button className="merch-action" type="button" disabled={publishing || !productId || currentMockups.length === 0} onClick={publishCurrentMockups} style={publishing || !productId || currentMockups.length === 0 ? disabledButtonStyle : primaryButtonStyle}>{publishing ? "Publishing…" : "Publish Complete Mockup Set"}</button>
      {currentMockups.length > 0 ? <p style={{ color: "#777", fontSize: "13px" }}>Publishes {currentMockups.length} images: front and back across all three garment colors.</p> : <p style={{ color: "#777", fontSize: "13px" }}>Open Mockup Studio to prepare the complete garment set.</p>}
      <div style={{ margin: "26px 0 18px", borderTop: "1px solid #2d2d2d" }} />
      <p style={{ margin: "0 0 14px", color: "#999", fontSize: "13px" }}>Or upload an externally approved Printful or garment mockup.</p>
      <label style={publishLabelStyle}>Approved garment mockup<input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => setMockupFile(event.target.files?.[0] || null)} style={publishInputStyle} /></label>
      {previewUrl ? <img src={previewUrl} alt="Selected approved garment mockup" style={publishPreviewStyle} /> : <p style={{ color: "#777" }}>No mockup selected.</p>}
      <button className="merch-action" type="button" disabled={publishing || !productId || !mockupFile} onClick={publish} style={publishing || !productId || !mockupFile ? disabledButtonStyle : primaryButtonStyle}>{publishing ? "Publishing…" : "Publish to Merch Engine"}</button>
      <p aria-live="polite" style={{ minHeight: "20px", color: message.includes("could not") ? "#ff8b87" : "#aaa" }}>{message}</p>
    </section>
  );
}

function MerchArtworkPreviewCard({ variant }) {
  const [previewMode, setPreviewMode] = useState("transparency");
  const [exporting, setExporting] = useState(false);
  const [status, setStatus] = useState("");
  const previewUrl = useSvgBlobUrl(variant.svgString);
  const metadata = identityArtworkMetadata[variant.placement];
  const svgFilename = metadata.filenames[variant.tone];
  const pngFilename = metadata.pngFilenames[variant.tone];

  const handleDownload = async (format) => {
    if (!variant.svgString || exporting) return;
    setExporting(true);
    setStatus("");
    try {
      await Promise.resolve();
      if (format === "svg") {
        downloadSvg(variant.svgString, svgFilename);
        setStatus("SVG downloaded.");
      } else {
        await exportSvgToPng({
          svgString: variant.svgString,
          filename: pngFilename,
          width: identityArtworkMetadata.width,
          height: identityArtworkMetadata.height
        });
        setStatus("PNG downloaded.");
      }
    } catch (error) {
      console.error(`[Merch Engine] ${variant.title} ${format.toUpperCase()} export failed:`, error);
      setStatus(`${format.toUpperCase()} export failed. Check the browser console for details.`);
    } finally {
      setExporting(false);
    }
  };

  const surfaceStyle = previewMode === "transparency"
    ? checkerboardStyle
    : { ...garmentSurfaceStyle, backgroundColor: variant.tone === "dark" ? "#242424" : "#e5ded1" };

  return (
    <article style={{ ...cardStyle, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "24px 24px 18px" }}>
        <p style={sectionLabelStyle}>Identity Tee</p>
        <h2 style={{ margin: "0 0 8px", fontSize: "clamp(23px, 3vw, 30px)" }}>{variant.title}</h2>
        <p style={{ margin: 0, color: "#999" }}>Recommended use: {variant.recommendedUse}</p>
      </div>

      <div style={{ padding: "0 18px" }}>
        <div style={surfaceStyle}>
          {variant.generationError ? (
            <div role="alert" style={errorStyle}>
              <strong>Artwork unavailable.</strong>
              <span>{variant.generationError}</span>
            </div>
          ) : previewUrl ? (
            <img src={previewUrl} alt={`${variant.title} transparent production artwork preview`} style={previewImageStyle} />
          ) : (
            <p style={{ color: "#777" }}>Preparing preview…</p>
          )}
        </div>

        <div role="group" aria-label={`${variant.title} preview background`} style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "12px" }}>
          <button className="merch-option" type="button" aria-pressed={previewMode === "transparency"} onClick={() => setPreviewMode("transparency")} style={previewMode === "transparency" ? selectedOptionButtonStyle : optionButtonStyle}>Transparency</button>
          <button className="merch-option" type="button" aria-pressed={previewMode === "garment"} onClick={() => setPreviewMode("garment")} style={previewMode === "garment" ? selectedOptionButtonStyle : optionButtonStyle}>Garment Tone</button>
        </div>
      </div>

      <dl style={detailsStyle}>
        <Detail label="Canvas" value={`${identityArtworkMetadata.width} × ${identityArtworkMetadata.height} px`} />
        <Detail label="Safe margin" value={`${identityArtworkMetadata.safeMargin} px`} />
        <Detail label="Recommended print width" value={`${metadata.recommendedPrintWidthInches} in`} />
        <Detail label="Supported colors" value={metadata.supportedGarmentColors[variant.tone].join(", ")} />
      </dl>

      <div className="merch-artwork-actions" style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "auto", padding: "0 24px 24px" }}>
        <button className="merch-action" type="button" disabled={exporting || !variant.svgString} aria-label={`Download ${variant.title} SVG`} onClick={() => handleDownload("svg")} style={exporting || !variant.svgString ? { ...disabledButtonStyle, flex: 1 } : { ...primaryButtonStyle, flex: 1 }}>Download SVG</button>
        <button className="merch-action" type="button" disabled={exporting || !variant.svgString} aria-label={`Download ${variant.title} PNG`} onClick={() => handleDownload("png")} style={exporting || !variant.svgString ? { ...disabledButtonStyle, flex: 1 } : { ...primaryButtonStyle, flex: 1, background: "transparent", borderColor: "#484848" }}>{exporting ? "Exporting…" : "Download PNG"}</button>
        <p aria-live="polite" style={{ width: "100%", minHeight: "20px", margin: "2px 0 0", color: status.includes("failed") ? "#ff8b87" : "#aaa", fontSize: "13px" }}>{status}</p>
      </div>
    </article>
  );
}

function useSvgBlobUrl(svgString) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!svgString) {
      setUrl("");
      return undefined;
    }
    const blobUrl = URL.createObjectURL(new Blob([svgString], { type: "image/svg+xml;charset=utf-8" }));
    setUrl(blobUrl);
    return () => URL.revokeObjectURL(blobUrl);
  }, [svgString]);
  return url;
}

function Detail({ label, value }) {
  return <div><dt style={{ color: "#777", fontSize: "10px", fontWeight: 900, letterSpacing: "1px", textTransform: "uppercase" }}>{label}</dt><dd style={{ margin: "6px 0 0", color: "#ddd", lineHeight: 1.45 }}>{value}</dd></div>;
}

function PreviewResponsiveStyles() {
  return <style>{`.merch-artwork-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:22px}.merch-artwork-actions button{min-width:180px}@media(max-width:760px){.merch-artwork-grid{grid-template-columns:1fr}}@media(max-width:480px){.merch-artwork-actions{flex-direction:column}.merch-artwork-actions button{width:100%;min-width:0}}`}</style>;
}

const checkerboardStyle = { aspectRatio: "5 / 6", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", border: "1px solid #343434", borderRadius: "12px", backgroundColor: "#ddd", backgroundImage: "conic-gradient(#bbb 25%, #eee 0 50%, #bbb 0 75%, #eee 0)", backgroundSize: "28px 28px" };
const garmentSurfaceStyle = { ...checkerboardStyle, backgroundImage: "none" };
const previewImageStyle = { display: "block", width: "100%", height: "100%", objectFit: "contain" };
const errorStyle = { display: "flex", flexDirection: "column", gap: "8px", maxWidth: "320px", padding: "22px", border: "1px solid #e10600", borderRadius: "10px", background: "#181818", color: "white", lineHeight: 1.5 };
const detailsStyle = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "20px", margin: "24px", padding: "20px", border: "1px solid #2d2d2d", borderRadius: "12px", background: "#151515" };
const publishLabelStyle = { display: "grid", gap: "8px", marginBottom: "16px", color: "#ddd", fontWeight: 800 };
const publishInputStyle = { width: "100%", boxSizing: "border-box", padding: "12px", border: "1px solid #333", borderRadius: "10px", background: "#111", color: "white" };
const publishPreviewStyle = { display: "block", width: "100%", maxWidth: "480px", maxHeight: "560px", margin: "20px 0", objectFit: "contain", border: "1px solid #333", borderRadius: "12px", background: "#0b0b0b" };

export default MerchArtworkPreview;
